<template>
    <div>
        <breadcrumbs />
        <v-card class="mt-4 pa-4" variant="outlined">
            <h1 class="text-h4 mb-4">{{ $t('reservation_statements') }}</h1>
            <v-row>
                <v-col cols="12" sm="3">
                    <v-select
                        v-model="selectedYear"
                        :items="availableYears"
                        label="Select Year"
                        variant="outlined"
                        density="comfortable"
                        hide-details
                    />
                </v-col>
                <v-col v-if="isTO1" cols="12" sm="3">
                    <v-select
                        v-model="selectedSupplierId"
                        :items="supplierOptions"
                        :label="$t('select_supplier')"
                        variant="outlined"
                        density="comfortable"
                        hide-details
                        :loading="isLoadingSuppliers"
                        item-title="name"
                        item-value="id"
                    />
                </v-col>
                <v-col :cols="12" :sm="isTO1 ? 6 : 9" class="d-flex justify-end align-center">
                    <v-btn color="primary" variant="outlined" prepend-icon="mdi-download" @click="downloadAllPdfs">
                        {{ $t('download_all_pdfs', {year: selectedYear}) }}
                    </v-btn>
                </v-col>
            </v-row>

            <v-data-table
                :headers="headers"
                :items="monthlyInvoices"
                :loading="isLoading"
                :items-per-page="-1"
                hide-default-footer
                class="mt-4"
            >
                <template v-slot:item.actions="{item}">
                    <div class="d-flex align-center">
                        <a class="statement-link me-1" @click="downloadStatementPDF(item)">
                            {{ $t('download_statement_pdf') }}
                        </a>
                        <span>|</span>
                        <a class="statement-link ms-1" @click="viewStatement(item)">
                            {{ $t('view_statement') }}
                        </a>
                    </div>
                </template>
                <template v-slot:item.amount="{item}">
                    <div>{{ $filters.priceFormat(item.amount, item.currency) }}</div>
                </template>
                <template v-slot:item.date="{item}">
                    <div>{{ $filters.dateFormat(item.date) }}</div>
                </template>
            </v-data-table>
        </v-card>
    </div>
</template>

<script setup lang="ts">
    import Breadcrumbs from '~src/components/Breadcrumbs.vue'
    import {ref, computed, onMounted, watch, nextTick} from 'vue'
    import {generateReservationStatementPDF} from '../utils/pdf-generator'
    import {generateSimplePDF} from '../utils/simple-pdf-generator'
    import {authStore} from '~/composables/store-accessor'
    import {format, startOfMonth, endOfMonth} from 'date-fns'

    const {$api, $toast} = useNuxtApp()
    const {t} = useI18n()
    const router = useRouter()
    const route = useRoute()

    function getCommissionPercentageFromSustainabilityLevel(sustainabilityLevel) {
        switch (sustainabilityLevel) {
            case 'ZERO':
                return 10
            case 'SUSTAINABLE':
                return 5
            case 'BASIC':
            default:
                return 15
        }
    }

    const isLoading = ref(false)
    const isLoadingSuppliers = ref(false)
    const orders = ref([])
    const monthlyInvoices = ref([])
    const totalCount = ref(0)
    const filterKey = ref(null)
    const suppliers = ref([])
    const selectedSupplierId = ref(null)

    // Check if user is TO1
    const isTO1 = computed(() => authStore.isTO1)

    // Check if user is a supplier
    const isSupplier = computed(() => authStore.isSupplier)

    // Get the current supplier name if the user is a supplier
    const currentSupplierName = computed(() => (isSupplier.value ? authStore.company?.name : null))

    // Get current date
    const currentDate = new Date()

    // Create supplier options with "All suppliers" at the top
    const supplierOptions = computed(() => {
        return [{id: null, name: t('all_suppliers')}, ...suppliers.value]
    })

    // Set default selected year to current year or 2025 (company start year) if current year is earlier
    const startYear = 2025 // Company started in 2025
    const currentYear = currentDate.getFullYear()
    const selectedYear = ref(currentYear >= startYear ? currentYear : startYear)

    // Company started in 2025, so only show 2025 and later years
    const availableYears = computed(() => {
        const years = []

        // Start from the company's founding year (2025) and go up to current year
        // If current year is before 2025, just show 2025
        if (currentYear >= startYear) {
            for (let year = currentYear; year >= startYear; year--) {
                years.push(year)
            }
        } else {
            years.push(startYear)
        }

        return years
    })

    const headers = computed(() => [
        {title: t('document_name'), key: 'documentName', align: 'start' as const},
        {title: t('document_number'), key: 'documentNumber', align: 'start' as const},
        {title: t('date'), key: 'date', align: 'start' as const},
        {title: t('actions'), key: 'actions', align: 'start' as const},
        {title: t('amount'), key: 'amount', align: 'end' as const},
    ])

    onMounted(async () => {
        // Check if there's a year parameter in the route query
        const yearParam = route.query.year
        if (yearParam) {
            const yearValue = parseInt(yearParam.toString(), 10)
            // Validate that the year is within the available range
            if (!isNaN(yearValue) && availableYears.value.includes(yearValue)) {
                selectedYear.value = yearValue
            }
        }

        if (isTO1.value) {
            await loadSuppliers()

            // Check if there's a supplierId query parameter
            const supplierIdParam = route.query.supplierId
            if (supplierIdParam) {
                // Convert to the same type for comparison (string)
                const supplierIdStr = supplierIdParam.toString()

                // Wait for suppliers to load before setting the selected supplier
                await nextTick()

                // Find the supplier in the loaded suppliers list
                const foundSupplier = suppliers.value.find(
                    supplier => supplier.id && supplier.id.toString() === supplierIdStr
                )

                // Set the selected supplier if found
                if (foundSupplier) {
                    selectedSupplierId.value = foundSupplier.id
                }
            }
        }

        await loadOrders()
    })

    // Watch for changes in the selected supplier
    watch(selectedSupplierId, async () => {
        await loadOrders()
    })

    // Load suppliers for TO1 users
    async function loadSuppliers() {
        try {
            isLoadingSuppliers.value = true
            const response = await $api.suppliers.get({
                limit: 100,
            })
            suppliers.value = response.suppliers || []
        } catch (error) {
            $toast.error(t('error_message.load_error'))
        } finally {
            isLoadingSuppliers.value = false
        }
    }

    // Helper function to normalize order ID for comparison with reservationNumber
    function normalizeOrderIdForComparison(orderId) {
        // Convert orderId to string and pad with leading zeros to match reservationNumber format
        // reservationNumber in transaction API response ALWAYS has length of 8
        return orderId.toString().padStart(8, '0')
    }

    // Helper function to check if a reservationNumber matches any of our order IDs
    function doesReservationNumberMatchOrderIds(reservationNumber, orderIds) {
        if (!reservationNumber) return false

        // reservationNumber is always 8 characters, so normalize all order IDs to 8 characters
        const normalizedIds = new Set(orderIds.map(id => normalizeOrderIdForComparison(id)))
        return normalizedIds.has(reservationNumber)
    }

    // Helper function to extract order ID from reservationNumber
    function extractOrderIdFromReservationNumber(reservationNumber) {
        // Remove leading zeros to get the actual order ID
        return parseInt(reservationNumber, 10)
    }

    async function loadOrders() {
        isLoading.value = true
        try {
            // Clear previous orders for fresh data
            orders.value = []
            monthlyInvoices.value = []

            // Create an array to store monthly summaries
            const monthlySummaries = []

            // Get the current date to determine which months to process
            const currentDate = new Date()
            const currentYear = currentDate.getFullYear()
            const currentMonth = currentDate.getMonth()

            // Determine the maximum month to process
            // If it's the current year, only process up to the current month
            // If it's a past year, process all 12 months
            const maxMonth = selectedYear.value === currentYear ? currentMonth : 11

            // Process each month of the selected year (up to maxMonth)
            for (let month = 0; month <= maxMonth; month++) {
                // Create start and end dates for the month
                const startDate = new Date(selectedYear.value, month, 1)
                const endDate = new Date(selectedYear.value, month + 1, 0) // Last day of the month

                // Format dates for API request (use same format as PDF generation)
                const startDateStr = format(startDate, 'yyyy-MM-dd')
                const endDateStr = format(endDate, 'yyyy-MM-dd')

                // Make API request for this month with pagination support
                // Use service end date (checkout) to ensure orders are only in one month

                // Initialize an array to hold all orders for this month
                let allMonthOrders = []
                let offset = 0
                let hasMoreOrders = true

                // Fetch orders in batches until we have all of them
                while (hasMoreOrders) {
                    // Prepare API request parameters
                    const requestParams = {
                        // Use exact date range for the month to ensure no overlap
                        serviceEndDateFrom: startDateStr,
                        serviceEndDateTo: endDateStr,
                        limit: 100, // Maximum allowed limit
                        offset: offset,
                        orderType: authStore.orderType || 'B2C',
                        responseType: 'SHORT',
                    }

                    // Add supplier filter if a specific supplier is selected (TO1 only)
                    if (isTO1.value && selectedSupplierId.value) {
                        requestParams.supplierId = selectedSupplierId.value
                    }

                    const response = await $api.orders.get(requestParams)

                    // If we got orders, add them to our collection
                    if (response.orders && response.orders.length > 0) {
                        allMonthOrders = [...allMonthOrders, ...response.orders]

                        // Check if we need to fetch more orders
                        const totalOrdersCount = response.ordersCount || 0

                        // If we've fetched all orders, stop the loop
                        if (allMonthOrders.length >= totalOrdersCount) {
                            hasMoreOrders = false
                        } else {
                            // Otherwise, update the offset for the next batch
                            offset += response.orders.length
                        }
                    } else {
                        // No orders returned, stop the loop
                        hasMoreOrders = false
                    }
                }

                // Skip if no orders for this month
                if (allMonthOrders.length === 0) {
                    continue
                }

                // Filter orders to get only the services for this specific month
                // This ensures we calculate totals for exactly the same services that appear in the month
                const monthlyReservations = []

                for (const order of allMonthOrders) {
                    // Process each service in the order
                    if (order.services && order.services.length > 0) {
                        for (const service of order.services) {
                            if (service.serviceType === 'ACCOMMODATION') {
                                // Check if the service's checkout date is in the current month
                                const checkOut = service.endDateTime ? new Date(service.endDateTime) : null
                                if (!checkOut) continue

                                // Only include services with checkout dates in the current month
                                if (checkOut.getFullYear() !== selectedYear.value || checkOut.getMonth() !== month) {
                                    continue
                                }

                                // Get price information
                                let originalAmount = 0

                                if (service.salesTerms && service.salesTerms.length > 0) {
                                    const clientTerms = service.salesTerms.find(term => term.type === 'CLIENT')
                                    if (clientTerms && clientTerms.price) {
                                        originalAmount = clientTerms.price.amount || 0
                                    }
                                }

                                // Determine room type and commission percentage
                                let commissionPercentage = 15 // Default for standard rooms
                                let sustainabilityLevel = 'BASIC'

                                // Check if room type information is available
                                if (service.serviceDetailsContainer?.accommodation?.rooms?.length > 0) {
                                    const room = service.serviceDetailsContainer.accommodation.rooms[0]

                                    // Use sustainability level directly from API response (SHORT response includes this field)
                                    sustainabilityLevel = room.sustainabilityLevel || 'BASIC'

                                    // Determine commission percentage based on sustainability level
                                    commissionPercentage =
                                        getCommissionPercentageFromSustainabilityLevel(sustainabilityLevel)
                                }

                                // Calculate commission amount
                                const commissionAmount = (originalAmount * commissionPercentage) / 100

                                // Add this service to monthly reservations
                                monthlyReservations.push({
                                    orderId: order.orderId,
                                    originalAmount: originalAmount,
                                    commissionAmount: commissionAmount,
                                    paymentCharge: 0, // Will be updated when PSP commission data is fetched
                                })
                            }
                        }
                    }
                }

                // Get unique order IDs that have services in this month
                const orderIdsWithServices = [...new Set(monthlyReservations.map(r => r.orderId))]

                // Initialize the monthly summary
                const monthlySummary = {
                    orders: allMonthOrders.filter(order => orderIdsWithServices.includes(order.orderId)),
                    orderIds: orderIdsWithServices,
                    totalOriginalAmount: 0,
                    totalFinalAmount: 0,
                    totalPaymentCharge: 0,
                    totalCommissionAmount: 0,
                    totalPspCommission: 0,
                    date: startDate,
                    currency: 'EUR',
                    monthYear: `${selectedYear.value}-${(month + 1).toString().padStart(2, '0')}`,
                }

                // Calculate totals from the filtered monthly reservations
                for (const reservation of monthlyReservations) {
                    monthlySummary.totalOriginalAmount += reservation.originalAmount
                    monthlySummary.totalCommissionAmount += reservation.commissionAmount
                    monthlySummary.totalPaymentCharge += reservation.paymentCharge
                }

                // Fetch PSP commission data for this month's orders
                if (orderIdsWithServices.length > 0) {
                    const filteredOrders = allMonthOrders.filter(order => orderIdsWithServices.includes(order.orderId))
                    const orderPspCommissions = await fetchPspCommissionForOrdersDetailed(
                        filteredOrders,
                        startDateStr,
                        endDateStr
                    )

                    // Assign PSP commission to reservations
                    // For multi-room orders (same orderId), only the first room should get the PSP commission
                    // Track which orderIds we've already assigned PSP commission to
                    const orderIdsWithAssignedFee = new Set()
                    let totalPaymentCharge = 0

                    monthlyReservations.forEach(reservation => {
                        const pspCommission = orderPspCommissions.get(reservation.orderId) || 0

                        // Only assign PSP commission to the first room in a multi-room order
                        if (!orderIdsWithAssignedFee.has(reservation.orderId)) {
                            reservation.paymentCharge = pspCommission
                            totalPaymentCharge += pspCommission
                            orderIdsWithAssignedFee.add(reservation.orderId)
                        } else {
                            // For subsequent rooms in the same order, set payment charge to 0
                            reservation.paymentCharge = 0
                        }
                    })

                    // Update the summary with total payment charges
                    monthlySummary.totalPaymentCharge = totalPaymentCharge
                    monthlySummary.totalPspCommission = totalPaymentCharge
                }

                // Calculate final totals
                monthlySummary.totalFinalAmount =
                    monthlySummary.totalOriginalAmount -
                    monthlySummary.totalPaymentCharge -
                    monthlySummary.totalCommissionAmount

                // Add to monthly summaries only if there are actual reservations (not just orders)
                if (monthlySummary.totalOriginalAmount > 0) {
                    monthlySummaries.push(monthlySummary)
                }
            }

            // Process monthly summaries into table data
            await processMonthlyData(monthlySummaries)
        } catch (error) {
            $toast.error(t('error_message.load_error'))
        } finally {
            isLoading.value = false
        }
    }

    async function processMonthlyData(monthlySummaries) {
        // Convert monthly summaries to array for v-data-table
        const monthlyInvoicesArray = monthlySummaries.map(summary => {
            // Format month name
            const monthName = format(summary.date, 'MMMM')

            // Create a unique document number for each month using month and year
            // This ensures no overlap between different months
            const monthNum = summary.date.getMonth() + 1
            const yearNum = summary.date.getFullYear()
            const documentNumber = `${monthNum}-${yearNum}`

            return {
                documentName: t('monthly_invoice', {month: monthName}),
                documentNumber: documentNumber,
                date: summary.date,
                amount: summary.totalFinalAmount, // Show the sum of final amounts for all reservations in the month
                currency: summary.currency,
                orderIds: summary.orderIds,
                monthYear: summary.monthYear,
                startDate: startOfMonth(summary.date),
                endDate: endOfMonth(summary.date),
            }
        })

        // Sort by date (newest first)
        monthlyInvoicesArray.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

        // Update the reactive reference
        monthlyInvoices.value = monthlyInvoicesArray

        // Store all orders from all months
        orders.value = monthlySummaries.flatMap(summary => summary.orders)
    }

    async function downloadInvoice(invoiceId: number | string, format = 'pdf') {
        try {
            // In a real implementation, this would use the appropriate API endpoint
            // Since we're using the online payments API which doesn't have a direct download endpoint,
            // we would need to implement this functionality

            if (format === 'csv') {
                // For CSV, we'll show a message for now
                $toast.info('CSV download will be implemented in a future update')
            } else {
                // For PDF, we'll try to generate a simple PDF
                const item = monthlyInvoices.value.find(inv => inv.documentNumber === invoiceId)
                if (item) {
                    await downloadStatementPDF(item)
                } else {
                    $toast.error('Invoice not found')
                }
            }
        } catch (error) {
            $toast.error(t('error_message.download_error'))
        }
    }

    // Fetch PSP commission data for individual orders (for PDF generation)
    async function fetchPspCommissionForOrdersDetailed(orders, startDateStr, endDateStr) {
        try {
            const orderPspCommissions = new Map() // Map orderId -> pspCommission
            const orderIds = orders.map(order => order.orderId)

            if (orderIds.length === 0) {
                return orderPspCommissions
            }

            // We'll use the flexible matching function instead of pre-creating a map

            // Fetch transactions data for the orders in this period
            let offset = 0
            let hasMoreTransactions = true

            while (hasMoreTransactions) {
                const transactionParams = {
                    limit: 100,
                    offset: offset,
                    orderType: authStore.orderType || 'B2C',
                    // Add timestamp to prevent caching of transaction data
                    _t: Date.now(),
                }

                // Add date range filter if needed
                if (startDateStr && endDateStr) {
                    transactionParams.dateFrom = startDateStr
                    transactionParams.dateTo = endDateStr
                }

                try {
                    // Ensure fresh data by adding cache-busting headers
                    const transactionResponse = await $api.onlinePayments.get(transactionParams, null, {
                        headers: {
                            'Cache-Control': 'no-cache, no-store, must-revalidate',
                            Pragma: 'no-cache',
                            Expires: '0',
                        },
                    })

                    if (
                        transactionResponse.onlinePaymentTransactions &&
                        transactionResponse.onlinePaymentTransactions.length > 0
                    ) {
                        // Map transactions to orders and store pspCommission
                        transactionResponse.onlinePaymentTransactions.forEach(transaction => {
                            if (doesReservationNumberMatchOrderIds(transaction.reservationNumber, orderIds)) {
                                // Use pspCommission field if available, otherwise default to 0
                                const pspCommission = transaction.pspCommission || 0

                                // Extract the actual order ID from the reservation number
                                const actualOrderId = parseInt(transaction.reservationNumber, 10)

                                // Sum up PSP commissions for the same order (in case of multiple transactions)
                                const existingCommission = orderPspCommissions.get(actualOrderId) || 0
                                orderPspCommissions.set(actualOrderId, existingCommission + pspCommission)
                            }
                        })

                        // Check if we need to fetch more transactions
                        if (transactionResponse.onlinePaymentTransactions.length < 100) {
                            hasMoreTransactions = false
                        } else {
                            offset += 100
                        }
                    } else {
                        hasMoreTransactions = false
                    }
                } catch (transactionError) {
                    hasMoreTransactions = false
                }
            }

            return orderPspCommissions
        } catch (error) {
            return new Map()
        }
    }

    async function downloadStatementPDF(item: any) {
        try {
            isLoading.value = true

            // Get the month and year from the item
            const monthDate = new Date(item.date)
            const formattedPeriod = format(monthDate, 'MMMM yyyy')

            // Get all orders that have services with checkout dates in this month
            // Ensure strict filtering by year and month to prevent orders appearing in multiple months
            const [yearStr, monthStr] = item.monthYear.split('-')
            const year = parseInt(yearStr, 10)
            const month = parseInt(monthStr, 10) - 1 // JavaScript months are 0-indexed

            // If we don't have orders in memory, fetch them from the API
            let monthOrders = []

            if (orders.value.length === 0) {
                // We need to fetch orders for this month

                // Create start and end dates for the month
                const startDate = new Date(year, month, 1)
                const endDate = new Date(year, month + 1, 0) // Last day of the month

                // Format dates for API request
                const startDateStr = format(startDate, 'yyyy-MM-dd')
                const endDateStr = format(endDate, 'yyyy-MM-dd')

                // Initialize variables for pagination
                let allFetchedOrders = []
                let offset = 0
                let hasMoreOrders = true

                // Fetch orders in batches until we have all of them
                while (hasMoreOrders) {
                    try {
                        // Prepare API request parameters
                        const requestParams = {
                            serviceEndDateFrom: startDateStr,
                            serviceEndDateTo: endDateStr,
                            limit: 100, // Maximum allowed limit
                            offset: offset,
                            orderType: authStore.orderType || 'B2C',
                            responseType: 'SHORT',
                        }

                        // Add supplier filter if a specific supplier is selected (TO1 only)
                        if (isTO1.value && selectedSupplierId.value) {
                            requestParams.supplierId = selectedSupplierId.value
                        }

                        const response = await $api.orders.get(requestParams)

                        // If we got orders, add them to our collection
                        if (response.orders && response.orders.length > 0) {
                            allFetchedOrders = [...allFetchedOrders, ...response.orders]

                            // Check if we need to fetch more orders
                            const totalOrdersCount = response.ordersCount || 0

                            // If we've fetched all orders, stop the loop
                            if (allFetchedOrders.length >= totalOrdersCount) {
                                hasMoreOrders = false
                            } else {
                                // Otherwise, update the offset for the next batch
                                offset += response.orders.length
                            }
                        } else {
                            // No orders returned, stop the loop
                            hasMoreOrders = false
                        }
                    } catch (error) {
                        hasMoreOrders = false
                    }
                }

                // Filter the fetched orders
                monthOrders = allFetchedOrders.filter(order => {
                    if (!order.services || order.services.length === 0) return false

                    // Check if any accommodation service has a checkout date in the selected month
                    return order.services.some(service => {
                        if (service.serviceType !== 'ACCOMMODATION' || !service.endDateTime) return false
                        const checkOutDate = new Date(service.endDateTime)
                        // Strict check by year and month
                        return checkOutDate.getFullYear() === year && checkOutDate.getMonth() === month
                    })
                })
            } else {
                // Use the orders we already have in memory
                monthOrders = orders.value.filter(order => {
                    if (!order.services || order.services.length === 0) return false

                    // Check if any accommodation service has a checkout date in the selected month
                    return order.services.some(service => {
                        if (service.serviceType !== 'ACCOMMODATION' || !service.endDateTime) return false
                        const checkOutDate = new Date(service.endDateTime)
                        // Strict check by year and month
                        return checkOutDate.getFullYear() === year && checkOutDate.getMonth() === month
                    })
                })
            }

            if (monthOrders.length === 0) {
                $toast.error(t('error_message.no_orders_found'))
                return
            }

            // Fetch PSP commission data for all orders in this month
            const startDateStr = format(new Date(year, month, 1), 'yyyy-MM-dd')
            const endDateStr = format(new Date(year, month + 1, 0), 'yyyy-MM-dd')
            const orderPspCommissions = await fetchPspCommissionForOrdersDetailed(monthOrders, startDateStr, endDateStr)

            // Process all orders to get reservation information
            const orderReservations = []

            // Process each order
            for (const order of monthOrders) {
                // Process each service in the order
                if (order.services && order.services.length > 0) {
                    for (const service of order.services) {
                        if (service.serviceType === 'ACCOMMODATION') {
                            const checkIn = service.startDateTime ? new Date(service.startDateTime) : null
                            const checkOut = service.endDateTime ? new Date(service.endDateTime) : null

                            if (!checkIn || !checkOut) continue

                            // Calculate nights
                            const nights = Math.round((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24))

                            // Get room type information
                            let commissionPercentage = 15 // Default for standard rooms
                            let roomTypeName = 'Standard'
                            let sustainabilityLevel = 'BASIC'

                            if (service.serviceDetailsContainer?.accommodation?.rooms?.length > 0) {
                                const room = service.serviceDetailsContainer.accommodation.rooms[0]
                                roomTypeName = room.roomTypeName || 'Standard'

                                // Use sustainability level directly from API response (SHORT response includes this field)
                                sustainabilityLevel = room.sustainabilityLevel || 'BASIC'

                                // Determine commission percentage based on sustainability level
                                commissionPercentage =
                                    getCommissionPercentageFromSustainabilityLevel(sustainabilityLevel)
                            }

                            // Get guest name from travelers
                            let guestName = 'Guest'
                            if (service.travelers && service.travelers.length > 0) {
                                const mainTraveler = service.travelers.find(t => t.isTourLead) || service.travelers[0]
                                if (mainTraveler.name && mainTraveler.name.length > 0) {
                                    const name = mainTraveler.name[0]
                                    guestName = `${name.firstName || ''} ${name.lastName || ''}`.trim()
                                }
                            }

                            // Get price information
                            let originalAmount = 0

                            if (service.salesTerms && service.salesTerms.length > 0) {
                                const clientTerms = service.salesTerms.find(term => term.type === 'CLIENT')
                                if (clientTerms && clientTerms.price) {
                                    originalAmount = clientTerms.price.amount || 0
                                }
                            }

                            // Calculate commission amount
                            const commissionAmount = (originalAmount * commissionPercentage) / 100

                            // Get PSP commission for this order
                            const pspCommission = orderPspCommissions.get(order.orderId) || 0

                            // Determine result status
                            let result = 'Stayed'
                            if (service.status) {
                                if (service.status.toLowerCase() === 'canceled') {
                                    result = 'Cancelled'
                                } else {
                                    result = service.status
                                }
                            }

                            // Add to reservations array
                            const reservation = {
                                bookNumber: `${order.orderId}`,
                                guestName: guestName,
                                checkIn: checkIn,
                                checkOut: checkOut,
                                nights: nights,
                                commissionPercentage: commissionPercentage,
                                result: result,
                                originalAmount: originalAmount,
                                finalAmount: originalAmount - pspCommission - commissionAmount,
                                paymentCharge: pspCommission, // Use real PSP commission from transactions API
                                commissionAmount: commissionAmount,
                                roomTypeName: roomTypeName,
                                sustainabilityLevel: sustainabilityLevel, // Add sustainability level for PDF generation
                                orderId: order.orderId, // Add orderId for consistency
                            }

                            orderReservations.push(reservation)
                        }
                    }
                }
            }

            // Validate reservation data before PDF generation
            if (orderReservations.length > 0) {
                // Add validation function similar to ReservationStatementPage
                const validateReservationData = (reservations, context = '') => {
                    const validation = {
                        context,
                        totalReservations: reservations.length,
                        totalOriginalAmount: 0,
                        totalFinalAmount: 0,
                        totalPaymentCharge: 0,
                        totalCommissionAmount: 0,
                        issues: [],
                    }

                    reservations.forEach((reservation, index) => {
                        // Validate required fields
                        if (!reservation.originalAmount && reservation.originalAmount !== 0) {
                            validation.issues.push(`Reservation ${index}: Missing originalAmount`)
                        }
                        if (!reservation.finalAmount && reservation.finalAmount !== 0) {
                            validation.issues.push(`Reservation ${index}: Missing finalAmount`)
                        }
                        if (reservation.paymentCharge === undefined || reservation.paymentCharge === null) {
                            validation.issues.push(`Reservation ${index}: Missing paymentCharge`)
                        }
                        if (!reservation.commissionAmount && reservation.commissionAmount !== 0) {
                            validation.issues.push(`Reservation ${index}: Missing commissionAmount`)
                        }

                        // Accumulate totals
                        validation.totalOriginalAmount += reservation.originalAmount || 0
                        validation.totalFinalAmount += reservation.finalAmount || 0
                        validation.totalPaymentCharge += reservation.paymentCharge || 0
                        validation.totalCommissionAmount += reservation.commissionAmount || 0
                    })

                    return validation
                }

                validateReservationData(orderReservations, `Monthly PDF - ${item.monthYear}`)

                // Sort reservations by order ID (ascending)
                orderReservations.sort((a, b) => {
                    const orderIdA = parseInt(a.bookNumber)
                    const orderIdB = parseInt(b.bookNumber)
                    return orderIdA - orderIdB
                })
            }

            // If we have reservation data, generate the PDF
            if (orderReservations.length > 0) {
                // Get supplier name if a specific supplier is selected or if user is a supplier
                let supplierName = null

                if (isTO1.value && selectedSupplierId.value) {
                    // TO1 user with selected supplier
                    const selectedSupplier = suppliers.value.find(s => s.id === selectedSupplierId.value)
                    if (selectedSupplier) {
                        supplierName = selectedSupplier.name
                    }
                } else if (isSupplier.value && currentSupplierName.value) {
                    // Supplier user
                    supplierName = currentSupplierName.value
                }

                // Create title with supplier name if applicable
                const pdfTitle = supplierName
                    ? `${t('monthly_invoice', {month: format(monthDate, 'MMMM yyyy')})} - ${supplierName}`
                    : t('monthly_invoice', {month: format(monthDate, 'MMMM yyyy')})

                // Create filename with supplier name if applicable
                let filename = `monthly-statement-${item.monthYear}.pdf`
                if (supplierName) {
                    const supplierSlug = supplierName.toLowerCase().replace(/[^a-z0-9]+/g, '-')
                    filename = `monthly-statement-${item.monthYear}-${supplierSlug}.pdf`
                }

                // Try to generate PDF with our simple generator
                try {
                    generateSimplePDF({
                        reservations: orderReservations,
                        currency: item.currency || 'EUR',
                        title: pdfTitle,
                        period: formattedPeriod,
                        filename: filename,
                    })
                    $toast.success(t('success_message.pdf_generated'))
                } catch (simplePdfError) {
                    // Fall back to the original generator
                    try {
                        generateReservationStatementPDF({
                            reservations: orderReservations,
                            currency: item.currency || 'EUR',
                            title: pdfTitle, // Use the same title with supplier name
                            period: formattedPeriod,
                            filename: filename, // Use the same filename with supplier name
                        })
                        $toast.success(t('success_message.pdf_generated'))
                    } catch (fallbackError) {
                        $toast.error(t('error_message.download_error'))
                    }
                }
            } else {
                // If no reservation data, show a message
                $toast.warning(t('error_message.no_reservation_data'))
            }
        } catch (error) {
            $toast.error(t('error_message.download_error'))
        } finally {
            isLoading.value = false
        }
    }

    async function downloadAllPdfs() {
        try {
            isLoading.value = true

            // Get the selected year
            const yearDate = new Date(selectedYear.value, 0, 1)
            const yearName = format(yearDate, 'yyyy')

            // Get the current date to determine which months to include
            const currentDate = new Date()
            const currentYear = currentDate.getFullYear()
            const currentMonth = currentDate.getMonth()

            // Create an array to hold all monthly data
            const monthlyData = []

            // Initialize monthly data for each month in the selected year
            // Only include months up to the current month if it's the current year
            const maxMonth = selectedYear.value === currentYear ? currentMonth : 11

            for (let month = 0; month <= maxMonth; month++) {
                monthlyData.push({
                    month,
                    monthName: format(new Date(selectedYear.value, month, 1), 'MMMM'),
                    reservations: [],
                    monthYear: `${selectedYear.value}-${month + 1}`,
                    startDate: new Date(selectedYear.value, month, 1),
                    endDate: new Date(selectedYear.value, month + 1, 0), // Last day of the month
                    currency: 'EUR',
                })
            }

            // Check if we need to fetch orders directly from the API
            // This is needed when a supplier is selected but we don't have orders in memory
            if (isTO1.value && selectedSupplierId.value && orders.value.length === 0) {
                // We'll fetch orders for each month directly from the API with the supplier filter
                for (const monthData of monthlyData) {
                    const startDate = monthData.startDate
                    const endDate = monthData.endDate

                    // Format dates for API request
                    const startDateStr = format(startDate, 'yyyy-MM-dd')
                    const endDateStr = format(endDate, 'yyyy-MM-dd')

                    // Initialize variables for pagination
                    let allFetchedOrders = []
                    let offset = 0
                    let hasMoreOrders = true

                    // Fetch orders in batches until we have all of them
                    while (hasMoreOrders) {
                        try {
                            // Prepare API request parameters
                            const requestParams = {
                                serviceEndDateFrom: startDateStr,
                                serviceEndDateTo: endDateStr,
                                limit: 100, // Maximum allowed limit
                                offset: offset,
                                orderType: authStore.orderType || 'B2C',
                                responseType: 'SHORT',
                                supplierId: selectedSupplierId.value,
                            }

                            const response = await $api.orders.get(requestParams)

                            // If we got orders, add them to our collection
                            if (response.orders && response.orders.length > 0) {
                                allFetchedOrders = [...allFetchedOrders, ...response.orders]

                                // Check if we need to fetch more orders
                                const totalOrdersCount = response.ordersCount || 0

                                // If we've fetched all orders, stop the loop
                                if (allFetchedOrders.length >= totalOrdersCount) {
                                    hasMoreOrders = false
                                } else {
                                    // Otherwise, update the offset for the next batch
                                    offset += response.orders.length
                                }
                            } else {
                                // No orders returned, stop the loop
                                hasMoreOrders = false
                            }
                        } catch (error) {
                            hasMoreOrders = false
                        }
                    }

                    // Process the fetched orders for this month
                    if (allFetchedOrders.length > 0) {
                        await processOrdersForMonth(allFetchedOrders, monthData)
                    }
                }
            } else {
                // Use the orders we already have in memory
                let filteredOrders = orders.value

                // Only apply supplier filtering if a specific supplier is selected
                if (isTO1.value && selectedSupplierId.value) {
                    // Filter orders that have the matching supplier ID
                    // The supplier ID might be in different properties depending on the API response
                    filteredOrders = orders.value.filter(order => {
                        // Try different possible locations for supplier ID
                        const matches =
                            (order.supplierId && order.supplierId.toString() === selectedSupplierId.value.toString()) ||
                            (order.supplier &&
                                order.supplier.id &&
                                order.supplier.id.toString() === selectedSupplierId.value.toString()) ||
                            // Add more possible locations based on the API response structure
                            (order.supplier &&
                                order.supplier.supplierId &&
                                order.supplier.supplierId.toString() === selectedSupplierId.value.toString())

                        return matches
                    })
                }

                // Process each order and assign to the appropriate month
                for (const order of filteredOrders) {
                    if (!order.created || !order.services || order.services.length === 0) {
                        continue
                    }

                    // Process each service in the order
                    for (const service of order.services) {
                        if (service.serviceType === 'ACCOMMODATION') {
                            const checkIn = service.startDateTime ? new Date(service.startDateTime) : null
                            const checkOut = service.endDateTime ? new Date(service.endDateTime) : null

                            if (!checkIn || !checkOut) continue

                            // Skip services not in the selected year (based on checkout date)
                            if (checkOut.getFullYear() !== selectedYear.value) {
                                continue
                            }

                            // If it's the current year, skip services from future months (based on checkout date)
                            if (selectedYear.value === currentYear && checkOut.getMonth() > currentMonth) {
                                continue
                            }

                            // Get the month this service belongs to (based on checkout date)
                            const serviceMonth = checkOut.getMonth()

                            // Find the corresponding month data
                            const monthData = monthlyData.find(m => m.month === serviceMonth)
                            if (monthData) {
                                await addServiceToMonth(order, service, monthData)
                            }
                        }
                    }
                }
            }

            // Helper function to process orders for a specific month
            async function processOrdersForMonth(orders, monthData) {
                for (const order of orders) {
                    if (!order.services || order.services.length === 0) continue

                    for (const service of order.services) {
                        if (service.serviceType !== 'ACCOMMODATION' || !service.endDateTime) continue

                        const checkOut = new Date(service.endDateTime)

                        // Skip if not in the correct month
                        if (checkOut.getFullYear() !== selectedYear.value || checkOut.getMonth() !== monthData.month) {
                            continue
                        }

                        await addServiceToMonth(order, service, monthData)
                    }
                }
            }

            // Helper function to add a service to a month's reservations
            async function addServiceToMonth(order, service, monthData) {
                const checkIn = service.startDateTime ? new Date(service.startDateTime) : null
                const checkOut = service.endDateTime ? new Date(service.endDateTime) : null

                if (!checkIn || !checkOut) return

                // Calculate nights
                const nights = Math.round((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24))

                // Get room type information
                let commissionPercentage = 15 // Default for standard rooms
                let roomTypeName = ''
                let sustainabilityLevel = 'BASIC'

                if (service.serviceDetailsContainer?.accommodation?.rooms?.length > 0) {
                    const room = service.serviceDetailsContainer.accommodation.rooms[0]
                    roomTypeName = room.roomTypeName || ''

                    // Use sustainability level directly from API response (SHORT response includes this field)
                    sustainabilityLevel = room.sustainabilityLevel || 'BASIC'

                    // Determine commission percentage based on sustainability level
                    commissionPercentage = getCommissionPercentageFromSustainabilityLevel(sustainabilityLevel)
                }

                // If no room type found, use a placeholder
                if (!roomTypeName) {
                    roomTypeName = t('standard_room', {default: 'Standard Room'})
                }

                // Get guest name from travelers
                let guestName = ''
                if (service.travelers && service.travelers.length > 0) {
                    const mainTraveler = service.travelers.find(t => t.isTourLead) || service.travelers[0]
                    if (mainTraveler.name && mainTraveler.name.length > 0) {
                        const name = mainTraveler.name[0]
                        guestName = `${name.firstName || ''} ${name.lastName || ''}`.trim()
                    }
                }

                // If no guest name found, use a placeholder
                if (!guestName) {
                    guestName = t('unnamed_guest', {default: 'Unnamed Guest'})
                }

                // Get price information
                let originalAmount = 0

                if (service.salesTerms && service.salesTerms.length > 0) {
                    const clientTerms = service.salesTerms.find(term => term.type === 'CLIENT')
                    if (clientTerms && clientTerms.price) {
                        originalAmount = clientTerms.price.amount || 0
                    }
                }

                // Calculate commission amount
                const commissionAmount = (originalAmount * commissionPercentage) / 100

                // Determine result status
                let result = ''
                if (service.status) {
                    if (service.status.toLowerCase() === 'canceled') {
                        result = t('cancelled', {default: 'Cancelled'})
                    } else {
                        result = service.status
                    }
                }

                // If no status found, use a default
                if (!result) {
                    result = t('completed', {default: 'Completed'})
                }

                // Add to the month's reservations array
                monthData.reservations.push({
                    bookNumber: `${order.orderId}`,
                    guestName: guestName,
                    checkIn: checkIn,
                    checkOut: checkOut,
                    nights: nights,
                    commissionPercentage: commissionPercentage,
                    result: result,
                    originalAmount: originalAmount,
                    finalAmount: originalAmount - 0 - commissionAmount, // paymentCharge will be updated later
                    paymentCharge: 0, // Will be updated with real PSP commission later
                    commissionAmount: commissionAmount,
                    roomTypeName: roomTypeName,
                    sustainabilityLevel: sustainabilityLevel, // Add sustainability level for PDF generation
                    orderId: order.orderId, // Add orderId for PSP commission lookup
                })
            }

            // Fetch PSP commission data for all months with reservations
            for (const monthData of monthlyData) {
                if (monthData.reservations.length > 0) {
                    const startDateStr = format(monthData.startDate, 'yyyy-MM-dd')
                    const endDateStr = format(monthData.endDate, 'yyyy-MM-dd')

                    // Get unique order IDs for this month
                    const monthOrderIds = [...new Set(monthData.reservations.map(r => r.orderId))]
                    const monthOrders = monthOrderIds.map(orderId => ({orderId}))

                    // Fetch PSP commission data
                    const orderPspCommissions = await fetchPspCommissionForOrdersDetailed(
                        monthOrders,
                        startDateStr,
                        endDateStr
                    )

                    // Update payment charges in reservations
                    // For multi-room orders (same orderId), only the first room should get the PSP commission
                    // Track which orderIds we've already assigned PSP commission to
                    const orderIdsWithAssignedFee = new Set()

                    monthData.reservations.forEach(reservation => {
                        const pspCommission = orderPspCommissions.get(reservation.orderId) || 0

                        // Only assign PSP commission to the first room in a multi-room order
                        if (!orderIdsWithAssignedFee.has(reservation.orderId)) {
                            reservation.paymentCharge = pspCommission
                            orderIdsWithAssignedFee.add(reservation.orderId)
                        } else {
                            // For subsequent rooms in the same order, set payment charge to 0
                            reservation.paymentCharge = 0
                        }

                        // Recalculate final amount to include the updated payment charge
                        reservation.finalAmount =
                            reservation.originalAmount - reservation.paymentCharge - reservation.commissionAmount
                    })
                }
            }

            // Generate a PDF for each month that has reservations
            let pdfGeneratedCount = 0
            let totalMonths = 0

            // Count months with reservations
            monthlyData.forEach(monthData => {
                if (monthData.reservations.length > 0) {
                    totalMonths++
                }
            })

            // If we have monthly data in the table but no reservations after filtering,
            // it means we need to use the data from the table directly
            if (totalMonths === 0 && monthlyInvoices.value.length > 0 && isTO1.value && selectedSupplierId.value) {
                // Get the selected supplier name
                const selectedSupplier = suppliers.value.find(s => s.id === selectedSupplierId.value)
                const supplierName = selectedSupplier ? selectedSupplier.name : selectedSupplierId.value

                // Process each month from the table data
                for (const monthInvoice of monthlyInvoices.value) {
                    try {
                        // Get the month and year
                        const monthDate = new Date(monthInvoice.date)
                        const formattedPeriod = format(monthDate, 'MMMM yyyy')

                        // Fetch the detailed data for this month
                        await downloadStatementPDF(monthInvoice)

                        pdfGeneratedCount++
                    } catch {
                        console.error('statement download/generation error')
                    }
                }

                // Show success message with count of PDFs generated
                if (pdfGeneratedCount > 0) {
                    $toast.success(
                        t('success_message.multiple_pdfs_generated', {
                            count: pdfGeneratedCount,
                            default: `${pdfGeneratedCount} PDFs generated successfully`,
                        })
                    )
                } else {
                    $toast.error(t('error_message.pdf_generation_failed'))
                }

                isLoading.value = false
                return
            }

            if (totalMonths === 0) {
                // Check if we have a supplier selected but no matching orders
                if (isTO1.value && selectedSupplierId.value) {
                    const selectedSupplier = suppliers.value.find(s => s.id === selectedSupplierId.value)
                    const supplierName = selectedSupplier ? selectedSupplier.name : selectedSupplierId.value
                    $toast.warning(
                        t('error_message.no_supplier_reservation_data', {
                            supplier: supplierName,
                            default: `No reservation data found for ${supplierName}`,
                        })
                    )
                } else {
                    $toast.warning(t('error_message.no_reservation_data'))
                }
                isLoading.value = false
                return
            }

            // Generate PDFs for each month with reservations
            for (const monthData of monthlyData) {
                if (monthData.reservations.length === 0) {
                    continue
                }

                const monthDate = new Date(selectedYear.value, monthData.month, 1)
                const formattedPeriod = format(monthDate, 'MMMM yyyy')

                try {
                    // Get supplier name if a specific supplier is selected or if user is a supplier
                    let supplierName = null

                    if (isTO1.value && selectedSupplierId.value) {
                        // TO1 user with selected supplier
                        const selectedSupplier = suppliers.value.find(s => s.id === selectedSupplierId.value)
                        if (selectedSupplier) {
                            supplierName = selectedSupplier.name
                        }
                    } else if (isSupplier.value && currentSupplierName.value) {
                        // Supplier user
                        supplierName = currentSupplierName.value
                    }

                    // Create title with supplier name if applicable
                    const pdfTitle = supplierName
                        ? `${t('monthly_invoice', {month: formattedPeriod})} - ${supplierName}`
                        : t('monthly_invoice', {month: formattedPeriod})

                    // Create filename with supplier name if applicable
                    let filename = `invoice-${selectedYear.value}-${monthData.month + 1}.pdf`
                    if (supplierName) {
                        const supplierSlug = supplierName.toLowerCase().replace(/[^a-z0-9]+/g, '-')
                        filename = `invoice-${selectedYear.value}-${monthData.month + 1}-${supplierSlug}.pdf`
                    }

                    // Try to generate PDF with our simple generator
                    try {
                        generateSimplePDF({
                            reservations: monthData.reservations,
                            currency: monthData.currency,
                            title: pdfTitle,
                            period: formattedPeriod,
                            filename: filename,
                        })
                        pdfGeneratedCount++
                    } catch (simplePdfError) {
                        // Fall back to the original generator
                        try {
                            // Use the same title with supplier name
                            generateReservationStatementPDF({
                                reservations: monthData.reservations,
                                currency: monthData.currency,
                                title: pdfTitle, // Use the same title variable from above
                                period: formattedPeriod,
                                filename: filename, // Use the same filename with supplier name
                            })
                            pdfGeneratedCount++
                        } catch {
                            console.error('statement download/generation error')
                        }
                    }
                } catch {
                    console.error('statement download/generation error')
                }
            }

            // Show success message with count of PDFs generated
            if (pdfGeneratedCount > 0) {
                $toast.success(
                    t('success_message.multiple_pdfs_generated', {
                        count: pdfGeneratedCount,
                        default: `${pdfGeneratedCount} PDFs generated successfully`,
                    })
                )
            } else {
                $toast.error(t('error_message.pdf_generation_failed'))
            }
        } catch (error) {
            $toast.error(t('error_message.download_error'))
        } finally {
            isLoading.value = false
        }
    }

    function viewStatement(item: any) {
        // Navigate to the reservation statement page with date range query parameters
        // Format dates as ISO strings (YYYY-MM-DD)
        const startDate = format(item.startDate, 'yyyy-MM-dd')
        const endDate = format(item.endDate, 'yyyy-MM-dd')

        // Create a unique identifier for the statement using the month and year
        // This ensures we don't have overlapping document numbers between months
        const statementId = `statement-${item.monthYear}`

        // Prepare query parameters
        const query: Record<string, string> = {
            startDate,
            endDate,
            monthYear: item.monthYear,
            year: selectedYear.value.toString(), // Add selected year to preserve it during navigation
        }

        // Add supplier ID and name if a specific supplier is selected (TO1 only) or if user is a supplier
        if (isTO1.value && selectedSupplierId.value) {
            // TO1 user with selected supplier
            const selectedSupplier = suppliers.value.find(s => s.id === selectedSupplierId.value)
            query.supplierId = selectedSupplierId.value
            if (selectedSupplier) {
                query.supplierName = selectedSupplier.name
            }
        } else if (isSupplier.value && currentSupplierName.value) {
            // Supplier user
            query.supplierName = currentSupplierName.value
        }

        router.push({
            name: 'reservationStatement',
            params: {reservationNumber: statementId},
            query,
        })
    }

    // Removed unused printStatement function

    // Watch for year changes to reload data
    watch(selectedYear, async () => {
        await loadOrders()
    })
</script>

<style scoped>
    a {
        color: var(--v-primary-base);
        cursor: pointer;
    }

    .statement-link {
        color: var(--zsh-primary);
        text-decoration: underline;
        cursor: pointer;
        transition: color 0.2s ease;
    }

    .statement-link:hover {
        color: var(--zsh-primary-dark);
        text-decoration: underline;
    }

    /* Print-specific styles */
    @media print {
        .v-btn {
            display: none !important;
        }

        .v-select {
            display: none !important;
        }

        .v-data-table-footer {
            display: none !important;
        }

        /* Ensure proper table styling */
        .v-data-table {
            width: 100%;
            border-collapse: collapse;
        }

        .v-data-table th,
        .v-data-table td {
            padding: 8px;
            text-align: left;
            border-bottom: 1px solid #ddd;
        }

        /* Hide action links in print view */
        .v-data-table a {
            display: none;
        }
    }
</style>
